import React, { useState, useEffect } from 'react';
import { X, Info, CheckCircle2, AlertTriangle, AlertCircle } from 'lucide-react';
import { AnnouncementService, SystemAnnouncement } from '../../services/announcementService';
import { useAuth } from '../../hooks/useAuth';

export function SystemAnnouncementBanner() {
  const [announcements, setAnnouncements] = useState<SystemAnnouncement[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const { user } = useAuth();

  useEffect(() => {
    loadAnnouncements();
    
    // Load dismissed announcements from localStorage
    const dismissedSet = AnnouncementService.getDismissedAnnouncements();
    setDismissed(dismissedSet);
  }, []);

  const loadAnnouncements = async () => {
    try {
      // Get user role from metadata or app_metadata
      const userRole = user?.user_metadata?.role || user?.app_metadata?.role || 'user';
      const active = await AnnouncementService.getActiveAnnouncements(userRole);
      setAnnouncements(active);
    } catch (error) {
      console.error('Error loading announcements:', error);
    }
  };

  const handleDismiss = (announcementId: string) => {
    AnnouncementService.dismissAnnouncement(announcementId);
    setDismissed(new Set([...dismissed, announcementId]));
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckCircle2 className="w-5 h-5" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5" />;
      case 'error':
        return <AlertCircle className="w-5 h-5" />;
      default:
        return <Info className="w-5 h-5" />;
    }
  };

  const getTypeStyles = (type: string) => {
    switch (type) {
      case 'success':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'error':
        return 'bg-red-50 border-red-200 text-red-800';
      default:
        return 'bg-blue-50 border-blue-200 text-blue-800';
    }
  };

  // Filter out dismissed announcements
  const visibleAnnouncements = announcements.filter(
    (ann: SystemAnnouncement) => !dismissed.has(ann.id)
  );

  if (visibleAnnouncements.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2 px-4 pt-4">
      {visibleAnnouncements.map((announcement: SystemAnnouncement) => (
        <div
          key={announcement.id}
          className={`rounded-xl border-2 p-4 flex items-start space-x-3 ${getTypeStyles(announcement.type)}`}
        >
          <div className="flex-shrink-0 mt-0.5">
            {getTypeIcon(announcement.type)}
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold mb-1">{announcement.title}</h4>
            <div 
              className="text-sm prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: announcement.message }}
            />
          </div>
          {announcement.dismissible && (
            <button
              onClick={() => handleDismiss(announcement.id)}
              className="flex-shrink-0 p-1 hover:bg-black/10 rounded-lg transition-colors duration-200"
              aria-label="Dismiss announcement"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

